import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
    get as _get,
} from 'lodash';
import clsx from 'clsx';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckboxOutlineIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckboxIcon from '@mui/icons-material/CheckBoxOutlined';

import ConfirmationDialog from '../../../../../components/confirmationdlg';
import Validator, { useNoisyValidate, checkValidity } from '../../../../../components/validator';

import PolicyService from '../../../../../data/policy-service';

const IS_MC2 = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';
import SystemService from '../../../../../data/system-service';

import Helpers from '../../../../../libraries/helpers';
import Auth, { ProfileContext } from '../../../../../data/auth-service';
import DataContext from '../../../../../data/data-context';
import useWindowSize from '../../../../../libraries/usewindowsize';
import Animations from '../../../../../libraries/animations';

const POLICY_TYPE = 'datanode_system_config';

const useStyles = makeStyles(theme => ({
    userButtons: {
        float: 'right',
        marginTop: theme.spacing(4),
        marginRight: theme.spacing(1),
        marginLeft: theme.spacing(1),
    },
    clearfix: {
        overflow: 'auto',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    header: {
        borderBottom: `1px solid rgba(${theme.palette.mode === 'dark' ? '81, 81, 81' : '224, 224, 224'}, 1)`,
    },
    infoBox: {
        border: `1px solid ${theme.palette.primary.main}`,
        background: theme.styled.paper.background,
        padding: theme.spacing(2),
    },
    filterContainer: {
        border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
        borderRadius: '5px',
        borderBottom: 'none',
        marginBottom: '-1px',
    },
    filterFields: {
        marginRight: theme.spacing(1),
        marginLeft: theme.spacing(1),
        minHeight: '32px',
    },
    ...Animations.Push(theme),
}));

const NEW_USER_DATA = {
    username: '',
    seclevel: 'authPriv',
    authtype: 'SHA',
    authpass: '',
    privtype: 'AES',
    privpass: '',
};

const DatanodeSNMPConfig = () => {
    const classes = useStyles();
    const params = useParams();
    const navigate = useNavigate();
    const { systemHealth } = useContext(DataContext);
    const snmpPerms = Auth.permissions.get('Cluster');
    const { height } = useWindowSize();
    const tableAPIRef = useApiRef();
    const { updateProfile, getProfile } = useContext(ProfileContext);
    const [columnWidth, setColumnWidth] = useState({});

    const [mode, setMode] = useState('edit');
    const [pushing, setPushing] = useState(false);

    // SNMP form state
    const [snmpTable, setSNMPTable] = useState(false);
    const [newUser, setNewUser] = useState(false);
    const [newUserData, setNewUserData] = useState(_cloneDeep(NEW_USER_DATA));
    const [editUser, setEditUser] = useState('');
    const [deleteUserDialog, setDeleteUserDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const snmpForm = useNoisyValidate({});

    const [snmp, setSNMP] = useState({
        syscontact: '',
        syslocation: '',
        communities: [],
        users: [],
    });
    const [original, setOriginal] = useState({});
    const [changes, setChanges] = useState(false);

    // Datanode assignment state
    const [datanodes, setDatanodes] = useState([]);
    const [filteredDatanodes, setFilteredDatanodes] = useState([]);
    const [selectedDatanodes, setSelectedDatanodes] = useState(IS_MC2 ? {} : []);
    const [originalAssignments, setOriginalAssignments] = useState([]);
    const [dnConfigMap, setDnConfigMap] = useState({});
    const [filter, setFilter] = useState(IS_MC2 ? {
        q: '',
        name: '',
        cmc: '',
        host: '',
        model: '',
        status: {
            critical: true,
            warning: true,
            normal: true,
        },
        policies: [],
        sort: '',
    } : { ip: '', model: '' });

    const getConfig = useCallback(() => {
        PolicyService.getDatanodePolicy(POLICY_TYPE, params.config).then(res => {
            let s = _cloneDeep(_get(res, 'data.snmp', {
                syscontact: '',
                syslocation: '',
                communities: [],
                users: [],
            }));
            if (!s.users) s.users = [];
            s.users = s.users.map(v => ({ ...v, id: Helpers.UniqueID() }));
            setOriginal(_cloneDeep(s));
            setSNMP(s);
            setSNMPTable(true);
        }, err => {
            if (typeof err === 'string') {
                toast.error(err);
            }
        });
    }, [params.config]);

    const getAssignments = useCallback(() => {
        PolicyService.getDatanodeAssignments(POLICY_TYPE).then(res => {
            const assigned = [];
            const configMap = {};
            if (res && typeof res === 'object' && !Array.isArray(res)) {
                Object.keys(res).forEach(configName => {
                    const dns = res[configName];
                    if (Array.isArray(dns)) {
                        dns.forEach(uuid => {
                            configMap[uuid] = configName;
                        });
                        if (configName === params.config) {
                            assigned.push(...dns);
                        }
                    }
                });
            }
            setDnConfigMap(configMap);
            if (IS_MC2) {
                const cmcKeyed = {};
                assigned.forEach(uuid => {
                    const dn = datanodes.find(d => d.uuid === uuid);
                    if (dn && dn.cmc_uuid) {
                        if (!cmcKeyed[dn.cmc_uuid]) {
                            cmcKeyed[dn.cmc_uuid] = { datanodes: [] };
                        }
                        if (!cmcKeyed[dn.cmc_uuid].datanodes.includes(uuid)) {
                            cmcKeyed[dn.cmc_uuid].datanodes.push(uuid);
                        }
                    }
                });
                setSelectedDatanodes(cmcKeyed);
            } else {
                setSelectedDatanodes(_cloneDeep(assigned));
            }
            setOriginalAssignments(_cloneDeep(assigned));
        }, err => {
            console.error('Unable to get datanode assignments', err);
        });
    }, [params.config, datanodes]);

    useEffect(() => {
        getConfig();
        getAssignments();
        setColumnWidth(getProfile('dnPush').widths || {});
    }, [getConfig, getAssignments]);

    // Auto-refresh assignments every 30s
    useEffect(() => {
        const interval = setInterval(() => {
            getAssignments();
        }, 30000);
        return () => clearInterval(interval);
    }, [getAssignments]);

    // Fetch global datanode list for MC2, fallback to systemHealth for CMC
    useEffect(() => {
        if (IS_MC2) {
            PolicyService.getGlobalDatanodes().then(dnList => {
                setDatanodes(dnList);
                setFilteredDatanodes(dnList);
            }, err => {
                console.error('Unable to get global datanode inventory', err);
                setDatanodes([]);
                setFilteredDatanodes([]);
            });
        } else {
            if (!systemHealth || !systemHealth.object) return;
            const nodes = _get(systemHealth, 'object.health_metrics.metadata_db.nodes', {});
            const grouped = {};
            Object.keys(nodes).forEach(nodeId => {
                const node = nodes[nodeId];
                const uuid = node.bricata_uuid;
                if (!uuid || node.bricata_type !== 'datanode') return;
                if (!grouped[uuid]) {
                    grouped[uuid] = {
                        id: uuid,
                        uuid: uuid,
                        ip: node.ip || '',
                        model: node.bricata_model || '',
                        version: node.bricata_version || '',
                        offline: node.offline || false,
                    };
                }
            });
            const dnList = Object.values(grouped);
            setDatanodes(dnList);
            setFilteredDatanodes(dnList);
        }
    }, [systemHealth]);


    useEffect(() => {
        if (IS_MC2) {
            runFilter(datanodes);
        } else {
            let filtered = [...datanodes];
            if (filter.ip) {
                filtered = filtered.filter(dn => dn.ip && dn.ip.includes(filter.ip));
            }
            if (filter.model) {
                filtered = filtered.filter(dn => dn.model && dn.model.toLowerCase().includes(filter.model.toLowerCase()));
            }
            setFilteredDatanodes(filtered);
        }
    }, [filter, datanodes]);

    const runFilter = (c) => {
        const s = _cloneDeep(c)
            .filter(v => {
                if (
                    _get(v, 'name', '').includes(filter.name) ||
                    _get(v, 'data.name', '').includes(filter.name)
                ) {
                    return v;
                }
                return null;
            })
            .filter(v => {
                if (_get(v, 'host', '').includes(filter.host)) return v;
                return null;
            })
            .filter(v => {
                if (_get(v, 'model', '').includes(filter.model)) return v;
                return null;
            })
            .filter(v => {
                if (_get(v, 'cmc', '').includes(filter.cmc)) return v;
                return null;
            });
        setFilteredDatanodes(s);
    };

    useEffect(() => {
        setChanges(!_isEqual(snmp, original));
        setSNMPTable(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snmp]);

    // SNMP form handlers
    const handleLocation = (e) => {
        setSNMP({ ...snmp, syslocation: e.currentTarget.value });
    };

    const handleContact = (e) => {
        setSNMP({ ...snmp, syscontact: e.currentTarget.value });
    };

    const handleCommunityStrings = (e) => {
        setSNMP({
            ...snmp,
            communities: e.currentTarget.value ? e.currentTarget.value.replace(/\s/g, '').split(',') : null,
        });
    };

    const handleSelectUser = (s) => {
        setSelectedUser(s.row);
    };

    const handleEditUser = () => {
        setTimeout(() => {
            setNewUserData(selectedUser);
            setNewUser(true);
            setEditUser(selectedUser.username);
        });
    };

    const handleOpenDeleteUser = () => {
        setDeleteUserDialog(true);
    };

    const handleCloseDeleteUser = () => {
        setDeleteUserDialog(false);
    };

    const handleDeleteUser = () => {
        setSNMPTable(false);
        const s = { ...snmp };
        const i = s.users.findIndex(v => v.username === selectedUser.username);
        s.users.splice(i, 1);
        setSNMP(s);
        setDeleteUserDialog(false);
    };

    const handleUsername = (e, validID, valid) => {
        snmpForm.set(current => ({ ...current, [validID]: valid }));
        setNewUserData({ ...newUserData, username: e.target.value });
    };

    const handleSeclevel = (e) => {
        setNewUserData({ ...newUserData, seclevel: e.target.value });
    };

    const handleAuthType = (e) => {
        setNewUserData({ ...newUserData, authtype: e.target.value });
    };

    const handleAuthPass = (e, validID, valid) => {
        snmpForm.set({
            ...snmpForm.get(),
            [validID]: editUser && e.target.value === '***' ? true : valid,
        });
        setNewUserData({ ...newUserData, authpass: e.target.value });
    };

    const handlePrivType = (e) => {
        setNewUserData({ ...newUserData, privtype: e.target.value });
    };

    const handlePrivPass = (e, validID, valid) => {
        snmpForm.set({
            ...snmpForm.get(),
            [validID]: editUser && e.target.value === '***' ? true : valid,
        });
        setNewUserData({ ...newUserData, privpass: e.target.value });
    };

    const handleAddNewUser = () => {
        setSNMPTable(false);
        const s = _cloneDeep(snmp);
        if (editUser) {
            const i = s.users.findIndex(v => v.username === editUser);
            s.users[i] = { ...newUserData, id: Helpers.UniqueID() };
        } else {
            s.users.push({ ...newUserData, id: Helpers.UniqueID() });
        }
        setSNMP(s);
        handleCancelNewUser();
    };

    const handleCancelNewUser = () => {
        setEditUser('');
        setNewUserData(_cloneDeep(NEW_USER_DATA));
        setNewUser(false);
    };

    const handleRemoveValidation = (id) => {
        const s = snmpForm.get();
        delete s[id];
        snmpForm.set(s);
    };

    const handleDownloadMIB = (file) => {
        SystemService.downloadMIB(file + '.txt').then(res => {
            const a = document.createElement('a');
            a.target = '_blank';
            a.download = `${file}.txt`;
            a.href = URL.createObjectURL(new Blob([res], { type: 'application/octet-stream' }), `${file}.txt`);
            a.click();
        });
    };

    // Push mode handlers
    const handlePushMode = () => {
        setMode('push');
    };

    const handleClosePush = () => {
        setMode('edit');
    };

    const getCMCUUID = (cmcName) => {
        const cmc = datanodes.find(dn => dn.cmc_uuid === cmcName);
        return cmc ? (cmc.cmc_uuid) : '';
    };

    // Returns true if datanode is originally assigned (locked/MC2-managed)
    const isDatanodeLocked = (uuid) => {
        return originalAssignments.includes(uuid);
    };

    const handleToggleDatanode = (uuid, cmc) => {
        if (isDatanodeLocked(uuid)) return; // Prevent toggling locked/originally assigned
        if (IS_MC2) {
            const s = { ...selectedDatanodes };
            const cmcUUID = getCMCUUID(cmc);
            if (!cmcUUID) return;
            if (!s[cmcUUID]) {
                s[cmcUUID] = { datanodes: [] };
            }
            if (s[cmcUUID].datanodes.includes(uuid)) {
                s[cmcUUID].datanodes.splice(s[cmcUUID].datanodes.indexOf(uuid), 1);
            } else {
                s[cmcUUID].datanodes.push(uuid);
            }
            setSelectedDatanodes(s);
        } else {
            setSelectedDatanodes(prev => {
                if (prev.includes(uuid)) {
                    return prev.filter(u => u !== uuid);
                }
                return [...prev, uuid];
            });
        }
    };

    const handleSelectAll = () => {
        if (IS_MC2) {
            const s = { ...selectedDatanodes };
            filteredDatanodes.forEach(v => {
                if (isDatanodeLocked(v.uuid)) return; // Skip locked/originally assigned
                // Robustly resolve CMC UUID
                const cmcUUID = getCMCUUID(v.cmc_uuid || v.cmc);
                if (!cmcUUID) return;
                if (!s[cmcUUID]) {
                    s[cmcUUID] = { datanodes: [] };
                }
                if (!s[cmcUUID].datanodes.includes(v.uuid)) {
                    s[cmcUUID].datanodes.push(v.uuid);
                }
            });
            setSelectedDatanodes(s);
        } else {
            const uuids = filteredDatanodes.filter(dn => !isDatanodeLocked(dn.uuid)).map(dn => dn.uuid);
            setSelectedDatanodes(prev => {
                const combined = new Set([...prev, ...uuids]);
                return [...combined];
            });
        }
    };

    const handleDeselectAll = () => {
        if (IS_MC2) {
            const s = { ...selectedDatanodes };
            filteredDatanodes.forEach(v => {
                if (isDatanodeLocked(v.uuid)) return; // Skip locked/originally assigned
                // Robustly resolve CMC UUID
                const cmcUUID = getCMCUUID(v.cmc_uuid || v.cmc);
                if (!cmcUUID || !s[cmcUUID] || !s[cmcUUID].datanodes) return;
                if (s[cmcUUID].datanodes.includes(v.uuid)) {
                    s[cmcUUID].datanodes.splice(s[cmcUUID].datanodes.indexOf(v.uuid), 1);
                }
            });
            setSelectedDatanodes(s);
        } else {
            const filterUuids = new Set(filteredDatanodes.filter(dn => !isDatanodeLocked(dn.uuid)).map(dn => dn.uuid));
            setSelectedDatanodes(prev => prev.filter(u => !filterUuids.has(u)));
        }
    };

    const handleUpdateFilter = (e) => {
        setFilter({ ...filter, [e.target.name]: e.target.value });
    };

    const handleColumnResize = () => {
        const widths = tableAPIRef.current.getAllColumns().reduce((a, c) => {
            a[c.headerName] = c.width;
            return a;
        }, {});
        updateProfile('dnPush', {
            ...getProfile('dnPush'),
            widths: widths,
        });
        setColumnWidth(widths);
    };

    const handleColumnReorder = () => {
        setTimeout(() => {
            const order = tableAPIRef.current.getAllColumns().reduce((a, c) => {
                a.push(c.headerName);
                return a;
            }, []);
            updateProfile('dnPush', {
                ...getProfile('dnPush'),
                columns: order,
            });
        });
    };

    // Returns columns for the datanode assignment grid (push mode), styled like sensor push page
    const getColumns = useCallback(() => {
        const tableOpts = {
            sortable: true,
            type: 'string',
            disableColumnMenu: true,
            hideable: false,
            hide: false,
        };
        const p = getProfile('dnPush');

        // Renderers for each column
        const tableRenderers = {
            ip: d => <div>{_get(d, 'row.ip', '')}</div>,
            cmc: d => <div>{_get(d, 'row.cmc', '') || _get(d, 'row.cmc_uuid', '')}</div>,
            model: d => <div>{_get(d, 'row.model', '')}</div>,
            version: d => <div>{_get(d, 'row.version', '')}</div>,
            currentConfig: d => <div>{dnConfigMap[_get(d.row, 'uuid', '')] || 'none'}</div>,
            enabled: d => (
                <FormControlLabel
                    label=""
                    control={
                        <Checkbox
                            checked={IS_MC2
                                ? Boolean(selectedDatanodes[getCMCUUID(_get(d.row, 'cmc_uuid', _get(d.row, 'cmc', '')))] && selectedDatanodes[getCMCUUID(_get(d.row, 'cmc_uuid', _get(d.row, 'cmc', '')))].datanodes.includes(_get(d.row, 'uuid', '')))
                                : selectedDatanodes.includes(_get(d.row, 'uuid', ''))
                            }
                            onChange={() => handleToggleDatanode(_get(d.row, 'uuid', ''), _get(d.row, 'cmc_uuid', _get(d.row, 'cmc', '')))}
                            disabled={isDatanodeLocked(_get(d.row, 'uuid', ''))}
                        />
                    }
                />
            ),
        };

        let c = [
            { ...tableOpts, width: columnWidth.IP || 0, ...(!columnWidth.IP ? { flex: 1 } : {}), headerName: 'IP', field: 'ip', renderCell: tableRenderers.ip },
            { ...tableOpts, width: columnWidth.Model || 0, ...(!columnWidth.Model ? { flex: 1 } : {}), headerName: 'Model', field: 'model', renderCell: tableRenderers.model },
            { ...tableOpts, width: columnWidth.Version || 0, ...(!columnWidth.Version ? { flex: 0.7 } : {}), headerName: 'Version', field: 'version', renderCell: tableRenderers.version },
            { ...tableOpts, width: columnWidth['Current config'] || 0, ...(!columnWidth['Current config'] ? { flex: 1 } : {}), headerName: 'Current config', field: 'currentConfig', renderCell: tableRenderers.currentConfig },
            { ...tableOpts, width: columnWidth.Enabled || 0, ...(!columnWidth.Enabled ? { flex: 0.5 } : {}), sortable: false, headerName: 'Enabled', field: 'enabled', renderCell: tableRenderers.enabled },
        ];
        if (IS_MC2) {
            c.splice(1, 0, {
                ...tableOpts,
                width: columnWidth.CMC || 0,
                ...(!columnWidth.CMC ? { flex: 0.5 } : {}),
                headerName: 'CMC',
                field: 'cmc',
                renderCell: tableRenderers.cmc,
            });
        }
        if (p.columns && p.columns.length) {
            c = p.columns.map(v => {
                for (let i = c.length; i--;) {
                    if (v === c[i].headerName) {
                        return c[i];
                    }
                }
                return null;
            }).filter(Boolean);
        }
        return c;
    }, [IS_MC2, columnWidth, getProfile, dnConfigMap, selectedDatanodes]);

    const handleDeploy = () => {
        setPushing(true);
        const data = {
            name: params.config,
            type: POLICY_TYPE,
            data: { snmp },
        };
        PolicyService.updateDatanodePolicy(POLICY_TYPE, params.config, data).then(() => {
            toast.success('Configuration saved successfully');
            setMode('edit');
            getConfig();

            // MC2: send CMC-keyed object, else flat array
            let payload = selectedDatanodes;
            if (IS_MC2) {
                // Remove empty CMCs
                payload = Object.keys(selectedDatanodes).reduce((acc, cmcUUID) => {
                    if (selectedDatanodes[cmcUUID].datanodes && selectedDatanodes[cmcUUID].datanodes.length) {
                        acc[cmcUUID] = { datanodes: selectedDatanodes[cmcUUID].datanodes };
                    }
                    return acc;
                }, {});
            }
            if ((IS_MC2 && Object.keys(payload).length) || (!IS_MC2 && payload.length)) {
                PolicyService.assignDatanodePolicy(POLICY_TYPE, params.config, payload).then(() => {
                    toast.success('Configuration pushed to datanode(s) successfully');
                    getAssignments();
                }, err => {
                    toast.error('Failed to push to datanodes');
                    console.error(err);
                });
            }
        }, err => {
            toast.error('Failed to save configuration');
            console.error(err);
        }).finally(() => { setPushing(false) });
    };

    const handleRevert = () => {
        getConfig();
        getAssignments();
    };

    const handleCancel = () => {
        navigate('/system/configuration/snmp/datanode');
    };

    return (
        <Grid container spacing={3}>
            {/* Top bar — visible in edit mode */}
            <Grid container item xs={12} justifyContent="space-between" alignItems="center"
                style={{ display: mode === 'push' ? 'none' : 'flex' }}>
                <Typography variant="h5">
                    Datanode SNMP Configuration: <code>{params.config}</code>
                </Typography>
                <Box display="flex">
                    {snmpPerms.write && <Button
                        className={classes.buttonRight}
                        variant="contained"
                        color="secondary"
                        aria-label="revert"
                        onClick={handleRevert}
                    >Revert</Button>}
                    {snmpPerms.write && <Button
                        variant="contained"
                        color="primary"
                        aria-label="push"
                        onClick={handlePushMode}
                        className={clsx({
                            [classes.buttonRight]: true,
                            [classes.pushAnimation]: changes,
                        })}
                    >Push</Button>}
                </Box>
            </Grid>

            {/* Push mode — datanode assignment grid */}
            <Grid item xs={12} style={{ display: mode === 'push' ? '' : 'none' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Box mr={2} display="inline-block">
                            <Typography variant="h4">Push Confirmation</Typography>
                        </Box>
                        <Box my={1} className={classes.infoBox} display="inline-block">
                            Select the datanodes you&apos;d like to deploy this configuration to.
                            Click Confirm to save and push.
                        </Box>
                        <Box py={1} px={2} className={classes.filterContainer} display="flex" justifyContent="space-between">
                            <Box display="flex" flexGrow="1">
                                <Box>
                                    <Typography variant="h6" style={{ display: 'inline-block', margin: '10px' }}>Filter</Typography>
                                </Box>
                                <Box flexGrow={1}>
                                    {IS_MC2 ? (
                                        <>
                                            <TextField variant="standard" className={classes.filterFields} type="text" label="CMC" name="cmc" value={filter.cmc} onChange={handleUpdateFilter} />
                                            <TextField variant="standard" className={classes.filterFields} type="text" label="Host" name="host" value={filter.host} onChange={handleUpdateFilter} />
                                            <TextField variant="standard" className={classes.filterFields} type="text" label="Model" name="model" value={filter.model} onChange={handleUpdateFilter} />
                                        </>
                                    ) : (
                                        <>
                                            <TextField variant="standard" className={classes.filterFields} type="text" label="Host" name="ip" value={filter.ip} onChange={handleUpdateFilter} />
                                            <TextField variant="standard" className={classes.filterFields} type="text" label="Model" name="model" value={filter.model} onChange={handleUpdateFilter} />
                                        </>
                                    )}
                                </Box>
                            </Box>
                            <Box display="flex" justifyContent="flex-end">
                                <Tooltip title="Select All"><IconButton onClick={handleSelectAll} size="large"><CheckboxIcon /></IconButton></Tooltip>
                                <Tooltip title="Deselect All"><IconButton onClick={handleDeselectAll} size="large"><CheckboxOutlineIcon /></IconButton></Tooltip>
                            </Box>
                        </Box>
                        <Box height={(height < 740 ? 265 : height - 500)}>
                            <DataGridPro
                                apiRef={tableAPIRef}
                                loading={pushing}
                                rows={filteredDatanodes}
                                columns={getColumns()}
                                disableMultipleSelection
                                disableSelectionOnClick
                                disableColumnFilter
                                disableVirtualization
                                onColumnResize={handleColumnResize}
                                onColumnOrderChange={handleColumnReorder}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box pt={2} mb={10} display="flex" justifyContent="flex-end">
                            <Box ml={2}><Button disabled={pushing} variant="contained" color="secondary" onClick={handleClosePush}>Cancel</Button></Box>
                            <Box ml={2}><Button disabled={pushing} variant="contained" color="primary" onClick={handleDeploy}>Confirm</Button></Box>
                        </Box>
                    </Grid>
                </Grid>
            </Grid>

            {/* Edit mode — SNMP form */}
            <Grid item xs={12} style={{ display: mode === 'push' ? 'none' : '' }}>
                <Paper elevation={3}>
                    <Box p={5}>
                        <Grid container spacing={2}>
                            <Grid item xs={5}>
                                <Box>
                                    <TextField
                                        disabled={!snmpPerms.write}
                                        aria-label="system location"
                                        label="System Location"
                                        fullWidth
                                        value={snmp.syslocation}
                                        onChange={handleLocation}
                                    />
                                </Box>
                                <Box mt={3}>
                                    <TextField
                                        disabled={!snmpPerms.write}
                                        aria-label="system contact"
                                        label="System Contact"
                                        fullWidth
                                        value={snmp.syscontact}
                                        onChange={handleContact}
                                    />
                                </Box>
                                <Box mt={3}>
                                    <TextField
                                        disabled={!snmpPerms.write}
                                        aria-label="read only community string"
                                        label="Read-Only Community String"
                                        helperText="Comma separated community strings"
                                        fullWidth
                                        value={snmp.communities || ''}
                                        onChange={handleCommunityStrings}
                                    />
                                </Box>
                                <Box mt={3}>
                                    <Box display="inline-block" mr={2}>
                                        <Button aria-label="Download BRICATA-MIB" download onClick={() => { handleDownloadMIB('BRICATA-MIB') }}>BRICATA-MIB</Button>
                                    </Box>
                                    <Box display="inline-block">
                                        <Button aria-label="Download BRICATA-DATANODE-MIB" download onClick={() => { handleDownloadMIB('BRICATA-DATANODE-MIB') }}>BRICATA-DATANODE-MIB</Button>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={7}>
                                <Box height={300}>
                                    {snmpTable && <DataGridPro
                                        components={{
                                            Header: !snmpPerms.write ? undefined : () => {
                                                return (
                                                    <Box display="flex" width="100%" justifyContent="flex-end" className={classes.header}>
                                                        <IconButton
                                                            variant="contained"
                                                            color="primary"
                                                            aria-label="edit user"
                                                            onClick={handleEditUser}
                                                            disabled={!selectedUser}
                                                            size="large">
                                                            <EditIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            variant="contained"
                                                            color="secondary"
                                                            aria-label="delete user"
                                                            onClick={handleOpenDeleteUser}
                                                            disabled={!selectedUser}
                                                            size="large">
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Box>
                                                );
                                            },
                                        }}
                                        rows={snmp.users}
                                        columns={[
                                            { sortable: true, flex: 1, type: 'string', disableColumnMenu: true, headerName: 'Username', field: 'username', renderCell: d => <div>{d.row.username}</div> },
                                            { sortable: true, flex: 1, type: 'string', disableColumnMenu: true, headerName: 'Security Level', field: 'seclevel', renderCell: d => <div>{d.row.seclevel}</div> },
                                            { sortable: true, flex: 1, type: 'string', disableColumnMenu: true, headerName: 'Auth Protocol', field: 'authtype', renderCell: d => <div>{d.row.authtype}</div> },
                                            { sortable: true, flex: 1, type: 'string', disableColumnMenu: true, headerName: 'Privacy Protocol', field: 'privtype', renderCell: d => <div>{d.row.privtype}</div> },
                                        ]}
                                        disableMultipleSelection
                                        onRowClick={handleSelectUser}
                                    />}
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box my={3} display={(newUser || !snmpPerms.write) ? 'none' : 'block'}>
                                    <Button className={classes.buttonRight} variant="contained" color="primary" aria-label="add read-only user" onClick={() => { setNewUser(!newUser) }}>Read-Only User +</Button>
                                </Box>
                                <Paper elevation={3}>
                                    <Box mt={3} p={5} display={newUser ? 'block' : 'none'}>
                                        <Grid container item xs={12} spacing={5}>
                                            <Grid item xs={6}>
                                                <Box>
                                                    {newUser && <Validator
                                                        valueType="snmpName"
                                                        errorText="SNMP username is invalid"
                                                        aria-label="username"
                                                        label="Username"
                                                        fullWidth
                                                        value={newUserData.username}
                                                        onChange={handleUsername}
                                                        onRemoval={handleRemoveValidation}
                                                    />}
                                                </Box>
                                                <Box mt={3}>
                                                    <FormControl fullWidth>
                                                        <InputLabel id="snmp-auth-protocol-label">Auth Protocol</InputLabel>
                                                        <Select
                                                            aria-label="auth protocol"
                                                            labelId="snmp-auth-protocol-label"
                                                            label="Auth Protocol"
                                                            value={newUserData.authtype}
                                                            onChange={handleAuthType}
                                                        >
                                                            <MenuItem value="SHA">SHA</MenuItem>
                                                            <MenuItem value="MD5">MD5</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                                <Box mt={3}>
                                                    <FormControl fullWidth>
                                                        <InputLabel id="snmp-privacy-protocol-label">Privacy Protocol</InputLabel>
                                                        <Select
                                                            aria-label="privacy protocol"
                                                            labelId="snmp-privacy-protocol-label"
                                                            label="Privacy Protocol"
                                                            value={newUserData.privtype}
                                                            onChange={handlePrivType}
                                                        >
                                                            <MenuItem value="AES">AES</MenuItem>
                                                            <MenuItem value="DES">DES</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box>
                                                    <FormControl fullWidth>
                                                        <InputLabel id="snmp-security-level-label">Security Level</InputLabel>
                                                        <Select
                                                            aria-label="security level"
                                                            labelId="snmp-security-level-label"
                                                            label="Security Level"
                                                            value={newUserData.seclevel}
                                                            onChange={handleSeclevel}
                                                        >
                                                            <MenuItem value="authPriv">authPriv</MenuItem>
                                                            <MenuItem value="authNoPriv">authNoPriv</MenuItem>
                                                            <MenuItem value="noAuthNoPriv">noAuthNoPriv</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                                <Box mt={3}>
                                                    {newUser && <Validator
                                                        valueType="length"
                                                        errorText="Must be a minimum of 8 characters"
                                                        inputProps={{ min: 8 }}
                                                        label="Auth Password Phrase"
                                                        aria-label="auth password phrase"
                                                        type="password"
                                                        fullWidth
                                                        value={newUserData.authpass}
                                                        onChange={handleAuthPass}
                                                        onRemoval={handleRemoveValidation}
                                                    />}
                                                </Box>
                                                <Box mt={3}>
                                                    {newUser && <Validator
                                                        valueType="length"
                                                        errorText="Must be a minimum of 8 characters"
                                                        inputProps={{ min: 8 }}
                                                        label="Privacy Password Phrase"
                                                        aria-label="privacy password phrase"
                                                        type="password"
                                                        fullWidth
                                                        value={newUserData.privpass}
                                                        onChange={handlePrivPass}
                                                        onRemoval={handleRemoveValidation}
                                                    />}
                                                </Box>
                                            </Grid>
                                        </Grid>
                                        <div className={classes.clearfix}>
                                            <Button
                                                className={classes.userButtons}
                                                variant="contained"
                                                color="primary"
                                                aria-label="add"
                                                onClick={handleAddNewUser}
                                                disabled={!checkValidity(snmpForm.get())}
                                            >
                                                {editUser ? 'Update' : 'Add'}
                                            </Button>
                                            <Button className={classes.userButtons} variant="contained" color="secondary" aria-label="cancel" onClick={handleCancelNewUser}>Cancel</Button>
                                        </div>
                                    </Box>
                                </Paper>
                            </Grid>
                            {snmpPerms.write && <Grid item xs={12}>
                                <Button className={classes.buttonRight} variant="contained" color="secondary" aria-label="cancel" onClick={handleCancel}>Cancel</Button>
                            </Grid>}
                        </Grid>
                    </Box>
                </Paper>
            </Grid>
            <ConfirmationDialog
                onClose={handleCloseDeleteUser}
                onOK={handleDeleteUser}
                open={deleteUserDialog}
                message={`Are you sure you want to remove the user ${selectedUser && selectedUser.username}?`}
                title="Delete User"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
        </Grid>
    );
};

export default DatanodeSNMPConfig;
